import React, { useCallback, useEffect, useRef } from 'react';
import { usePrint } from '../PrintProvider';
import { usePatientContext } from '../patinetProvider';
import {  getReportDataProcedureList, getReportDataDrugs } from '../PrintProvider/helper.print';
import {
  Modal,
  Box,
  DialogTitle,
  IconButton,
  DialogContent,
  DialogActions,
  Typography,
  Button
} from '@mui/material';
import TreatmentPlanProcedure from './treatmentPlanProcedure';
import { PaginatedResponse } from '../../types';
import { ReactComponent as PrinterIcon } from '../../assets/SVG/close-black.svg';
import { useVerboseLog } from '../../utils/debug';
import TreatmentPlanDrug from './treamentPlanDrug';
import { useAlert } from '../alertProvider';
import SemenAnalysisReport from './semenAnalysis';
import SemenFreezingReport from './semenFreezing';
import { getSemenAnalysisReport, SemenAnalysisReportResponse } from '../SemanAnalysisComponent/SemanAnalysisComponentHelper';

const PrintModalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth:'994px',
  maxWidth:'1024px',
  width: 'auto', 
  minHeight:'90vh', // A4 height
  height: 'auto', // A4 height
  boxShadow: 24,
  p: 4,
  overflow: 'hidden',
  borderRadius: 2,
  display: 'flex',
  flexDirection: 'column',
};

interface PrintReportProps {
  handleClose: () => void; // Function to close the print report
}
const PrintReport = ({ handleClose }: PrintReportProps) => {

  const verboseLog = useVerboseLog(); // Custom hook for logging
  const {addAlert} = useAlert();  
  const { TabOption } = usePrint(); // Use the custom hook to access the context
  const { patientId } = usePatientContext(); // Use the custom hook to access the patient context
  const [printData, setPrintData] = React.useState<object[]>([]); // State to hold the print report data
  // Add any additional logic or state management here if needed

  const getPrintAPI = useCallback(() => {
    // Implement the logic to fetch the print report data from the API
    if (TabOption.tabName === 'treatment-plan') {
      // Fetch data for treatment plan
      if (TabOption.subOption === 'procedure_list') {
        // Fetch data for procedure list
        getReportDataProcedureList({ url: `cycle/treatment-report/patient/${patientId}/` })
          .then((response: PaginatedResponse<object[]>) => {
            verboseLog('Response:', response); // Log the response for debugging
            setPrintData(
              response && Array.isArray(response) ? [response[0]] : []
            ); // Set the fetched data to state
          })
          .catch((error) => {
            verboseLog('Error fetching procedure list:', error); // Log the error for debugging
          });
      } else if (TabOption.subOption === 'drug_list') {
        // Fetch data for drug list
        getReportDataDrugs({ url: 'patient-drugs/', patientId: patientId.toString() ?? '' }).then((response: PaginatedResponse<object[]>) => {
          verboseLog('Response:', response.objects); // Log the response for debugging
          setPrintData(
            response.objects ?? []
          ); // Set the fetched data to state
        }).catch((error) => {
          verboseLog('Error fetching drugs:', error); // Log the error for debugging
        });
      }
    }
    else if (TabOption.tabName === 'semen-analysis') {
      if(TabOption.reportOptions && TabOption.reportOptions.length > 0){        
        getSemenAnalysisReport(TabOption.reportOptions[0]?.toString() ?? '').then((response: SemenAnalysisReportResponse)=>{
          verboseLog('Response:', response); // Log the response for debugging          
          setPrintData([response]);
        }).catch((error) => {
          verboseLog('Error fetching collection details:', error); // Log the error for debugging
        });
      }else{
        addAlert('Please select a sample to print', 'error'); // Show an alert if no sample is selected
      }
    }
    else if (TabOption.tabName === 'semen-freezing') {
      verboseLog('TabOption', TabOption);
      if(TabOption.reportOptions && TabOption.reportOptions.length > 0){
        setPrintData(TabOption.reportOptions.filter((item: unknown) => item !== null) as object[]);
      }else{
        addAlert('Please select a cycle to print report', 'error'); // Show an alert if no sample is selected
      }
    }
    else{
      return;
    }
  }, [TabOption, patientId, verboseLog, addAlert]);
  useEffect(() => {
    getPrintAPI(); // Call the function to fetch data when the component mounts or when TabOption changes
  }, [TabOption, getPrintAPI]);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handlePrint = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus();
      iframeRef.current.contentWindow.print();
    }
  };
  return (
    <div>
      {printData?.length > 0 && (
        <Modal
          open={true}
          onClose={handleClose}
          disableEscapeKeyDown
          sx={PrintModalStyle}
          slotProps={{
            backdrop: {
              sx: {
                backgroundColor: '#fff',
              },
            },
          }}
        >
          <React.Fragment>
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p:2,
                pr:0
              }}
            >
              {`${TabOption.tabName?.split('-').join(' ').toLocaleUpperCase() ?? ''} - ${TabOption.subOption?.split('_').join(' ').toLocaleUpperCase() ?? ''} Report`}
              <IconButton onClick={handleClose}>
                <PrinterIcon style={{ width: '20px', height: '20px' }} />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box 
                sx={{ 
                  width: '100%', 
                  height: '100%',
                  border: '2px solid #ccc',               
                  borderRadius: '8px',
                  p:1
                }}>                               
                {TabOption.tabName === 'treatment-plan' &&
                  TabOption.subOption === 'procedure_list' && (
                  <TreatmentPlanProcedure data={printData[0]} iframeRef={iframeRef} />
                )}
                {TabOption.tabName === 'treatment-plan' &&
                  TabOption.subOption === 'drug_list' && (
                  <TreatmentPlanDrug data={printData} iframeRef={iframeRef} />
                )}
                {TabOption.tabName === 'semen-analysis' &&(
                  <SemenAnalysisReport data={printData[0] as SemenAnalysisReportResponse} iframeRef={iframeRef} />
                )}
                {TabOption.tabName === 'semen-freezing' &&(
                  <SemenFreezingReport data={printData[0]} iframeRef={iframeRef} />
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
              <Button
                onClick={handlePrint}
                variant="contained"
                sx={{
                  bgcolor: 'secondary.contrastText',
                  border: '1px solid',
                  borderColor: 'primary.contrastText',                                    
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: 'primary.contrastText' }}
                  fontWeight={700}
                  px={1}
                  py={0.5}
                >
                  Preview
                </Typography>
              </Button>
            </DialogActions>
          </React.Fragment>
        </Modal>
      )}
    </div>
  );
};
export default PrintReport;
